import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import IcomoonReact from 'icomoon-react';

import LeaderboardTemplateCard from './LeaderboardTemplateCard';
import LeaderboardZeroState from './LeaderboardZeroState';

import { showErrorToast } from '../../../shared/utils/toastManager';

import { addLeaderboard, setModalOpen } from '../ducks/leaderboardActions';
import { filterAndSortByGroups } from '../helpers';

import iconSet from '../../../shared/images/teambuildr-selection.json';

const GridOuter = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const AddCardPadder = styled('div')`
  padding: 10px;
`;

const AddCard = styled('div')`
  width: 300px;
  height: 250px;
  background: #ffffff;
  padding: 20px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  border-radius: 8px;
  font-family: 'Nunito Sans';
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);

  &:hover {
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.15);
  }
`;

const AddCardTopSpacer = styled('div')`
  height: 19.5px;
  flex-shrink: 0;
`;

const PlusIconContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  width: 52px;
  height: 52px;
  background: rgba(23, 135, 251, 0.6);
`;

const AddCardLabel = styled('div')`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
  text-align: center;
  color: #333;
`;

const GridContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  padding: 10px 20px;
`;

const LeaderboardTemplatesGrid = ({ searchTerm }) => {
  const templates = useSelector((state) => state.leaderboard.data.templates);
  const activeFilters = useSelector((state) => state.leaderboard.ui.activeFilters);
  const accountCode = useSelector((state) => state.auth.data.currentUser.accountCode);
  const dispatch = useDispatch();

  const { groups: filterGroups } = activeFilters;
  const hasActiveFilters = filterGroups.length > 0 || searchTerm.length > 0;

  const bySearch = searchTerm.length > 0
    ? templates.filter((t) => t.exerciseName.toLowerCase().includes(searchTerm.toLowerCase()))
    : templates;

  const filtered = filterGroups.length > 0
    ? filterAndSortByGroups(bySearch, filterGroups)
    : bySearch;

  const showNoResults = hasActiveFilters && filtered.length === 0;
  const showZeroState = templates.length === 0;
  const showEmptyState = showZeroState || showNoResults;

  const emptyHeading = showZeroState ? 'Rank your athletes' : 'No results found';
  const emptySubtext = showZeroState
    ? 'Build a leaderboard for any lift, SAQ+C event, or circuit — then save your favorites so they\'re always one click away.'
    : 'No saved leaderboards match your current filters. Try adjusting your search or create a new leaderboard.';

  const handleAddNew = () => {
    dispatch(setModalOpen(true, 'Create Leaderboard'));
  };

  const handleLoadTemplate = async (template) => {
    const { id, ...formValues } = template;
    try {
      await dispatch(addLeaderboard(
        { ...formValues, accountCode },
        true,
        id,
      ));
    } catch (err) {
      showErrorToast(err?.response?.headers?.message || 'Failed to load leaderboard. Please try again.');
    }
  };

  const createCard = (
    <AddCardPadder onClick={handleAddNew}>
      <AddCard>
        <AddCardTopSpacer />
        <PlusIconContainer>
          <IcomoonReact iconSet={iconSet} size={25} icon='plus' color='white' />
        </PlusIconContainer>
        <AddCardLabel>Create New Leaderboard</AddCardLabel>
      </AddCard>
    </AddCardPadder>
  );

  return (
    <GridOuter>
      {showEmptyState && (
        <LeaderboardZeroState
          heading={emptyHeading}
          subtext={emptySubtext}
          buttonLabel='+ New Board'
          onButtonClick={handleAddNew}
        />
      )}
      {!showEmptyState && (
        <GridContainer>
          {createCard}
          {filtered.map((template) => (
            <LeaderboardTemplateCard
              key={template.id}
              template={template}
              onLoad={handleLoadTemplate}
            />
          ))}
        </GridContainer>
      )}
    </GridOuter>
  );
};

LeaderboardTemplatesGrid.defaultProps = {
  searchTerm: '',
};

LeaderboardTemplatesGrid.propTypes = {
  searchTerm: PropTypes.string,
};

export default LeaderboardTemplatesGrid;
